import React, { useState, useEffect, useCallback, useMemo } from 'react';
import loadable from '@loadable/component';
import { Helmet } from "react-helmet";
import { sharedLoggerApi, sharedConstants } from 'shared-helpers';

// api
import startupApi from '/src/apis/startupApi';
import ecosystemApi from '/src/apis/ecosystemApi';
import fundingApi from '/src/apis/fundingApi';

// constants
import { HUBSPOT_FORM_SOURCE, CONTACT_FORM_TYPE } from '/src/lib/constants';
import { WEB_URL, PUBLIC_IMG_URL } from '/src/lib/endpoints';
import { SALES_CONTACT_SUCCESS_MESSAGE } from '/src/lib/messages';
import { CLIENT_LOGOS } from '/src/lib/constants/logoConstants.js';

// helpers
import AmplitudeHelperClass from '/src/lib/amplitudeHelper';
import { formatCount, withBootstrapPlugin } from '/src/lib/helpers';
import { handleRedirectMessage } from '/src/components/Reusable/toast';

// components
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
const SuccessPopup = loadable(() => import(/* webpackChunkName: "SuccessPopup" */ '/src/components/Reusable/SuccessPopup'));
const VideoHandler = loadable(() => import(/* webpackChunkName: "VideoHandler" */ '/src/components/Reusable/VideoHandler/VideoHandler'));
const ContactForm = loadable(() => import(/* webpackChunkName: "Contact" */ '/src/components/ContactForms/ContactForm'));

// Constants
const DEFAULT_COUNTS = {
    startups: '30,000+',
    investors: '14,000+',
    funds: '500+'
};

const amplitudeHelper = new AmplitudeHelperClass();
const LoggerApi = sharedLoggerApi;
const TAXONOMIES = sharedConstants.TAXONOMIES;

const LandingPageComponent = ({
    customPageMetadata,
    contactFormConfig,
    className = 'directory-landing-container',
    customFeaturesData = null,
    showVideoSection = true,
    location
}) => {
    const [totalStartups, setTotalStartups] = useState(DEFAULT_COUNTS.startups);
    const [totalInvestors, setTotalInvestors] = useState(DEFAULT_COUNTS.investors);
    const [totalFunds, setTotalFunds] = useState(DEFAULT_COUNTS.funds);
    const [isNarrowViewport, setIsNarrowViewport] = useState(false);

    const featuresData = useMemo(() => {
        if (customFeaturesData) {
            return customFeaturesData({ totalStartups, totalInvestors, totalFunds });
        }

        return [];
    }, [totalStartups, totalInvestors, totalFunds, customFeaturesData]);

    const pageMetadata = useMemo(() => {
        if (customPageMetadata) {
            return customPageMetadata({ totalStartups, totalInvestors, totalFunds });
        }

        return {
            title: 'Your Gateway to the VC and PE Ecosystem',
            description: `Gain unparalleled access to verified data on ${totalStartups} startups, ${totalInvestors} investors and ${totalFunds} venture and growth funds. Track deals, trends and opportunities, all from one powerful platform.`,
            url: '/directories'
        };
    }, [totalStartups, totalInvestors, totalFunds, customPageMetadata, customPageMetadata]);

    const showContactForm = useCallback(() => {
        amplitudeHelper.trackEvent(customPageMetadata.amplitudeEvent || 'Clicked Contact Sales from Landing Page', {
            landing_page_type: customPageMetadata.landingPageType || 'Generic'
        });

        withBootstrapPlugin('modal', ($) => $('#contactFormModal').modal('show'));

        if (typeof window.jQuery === 'undefined' || !window.jQuery.fn?.modal) {
            document.getElementById('contactForm')?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [customPageMetadata.amplitudeEvent, customPageMetadata.landingPageType]);

    const handleNavigation = useCallback((url) => {
        if (url) {
            window.location.href = url;
        } else {
            showContactForm();
        }
    }, []);

    useEffect(() => {
        // Fetch counts
        getDirectoryCounts()

        // Check for redirect params without showing toast on landing page
        handleRedirectMessage(location, false);

        const handleResize = () => {
            setIsNarrowViewport(window.innerWidth <= 933);
        };

        handleResize();
        window.addEventListener('resize', handleResize);


        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getDirectoryCounts = useCallback(async () => {
        try {
            const [startupsResponse, investorsResponse, fundsResponse] = await Promise.allSettled([
                startupApi.getStartupsCount(),
                ecosystemApi.getEcosystemsCount(),
                fundingApi.getLPFundingRoundsCount()
            ]);

            // Handle startups count
            if (startupsResponse.status === 'fulfilled' && startupsResponse.value > 0) {
                setTotalStartups(formatCount(startupsResponse.value));
            } else {
                LoggerApi.logError('getStartupsCount', customPageMetadata.componentName || 'LandingPageComponent', startupsResponse.reason);
            }

            // Handle investors count
            if (investorsResponse.status === 'fulfilled' && investorsResponse.value > 0) {
                setTotalInvestors(formatCount(investorsResponse.value));
            } else {
                LoggerApi.logError('getEcosystemsCount', customPageMetadata.componentName || 'LandingPageComponent', investorsResponse.reason);
            }

            // Handle funds count
            if (fundsResponse.status === 'fulfilled' && fundsResponse.value?.count > 0) {
                setTotalFunds(formatCount(fundsResponse.value.count));
            } else {
                LoggerApi.logError('getLPFundingRoundsCount', customPageMetadata.componentName || 'LandingPageComponent', fundsResponse.reason);
            }
        } catch (error) {
            LoggerApi.logError('getDirectoryCounts', customPageMetadata.componentName || 'LandingPageComponent', error);
        }
    }, [customPageMetadata.componentName]);

    const FeatureSection = useCallback(({ feature, index }) => {
        const isEven = index % 2 === 1;
        const isEvenAndLast = isEven && featuresData?.length === index + 1;
        const containerClass = `feature-item-container ${isEven ? `feature-item-container-right ${isEvenAndLast && 'mb-0'}` : ''}`;
        const imageContainerClass = `feature-item ${isEven ? 'right-ellipse-image-container' : 'left-ellipse-image-container'}`;
        const ellipseClass = isEven ? 'left-feature-ellipse' : 'first-feature-ellipse';
        const imageClass = isEven ? 'right-feature-image' : 'left-feature-image';

        return (
            <div className={containerClass}
                key={feature.id || index}
            >
                {(!isEven || (isNarrowViewport && isEven)) && (
                    <div className={imageContainerClass}>
                        <div className={ellipseClass} aria-hidden="true"></div>
                        <div className={imageClass}>
                            <div className='img-container'>
                                <img
                                    src={feature.image}
                                    alt={feature.altText}
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className={`feature-item ${isEven ? 'right-content' : ''}`}>
                    <div className='content'>
                        <h2>{feature.title}</h2>
                        <p>{feature.description}</p>
                        <button
                            className="btn btn-main btn-lg"
                            onClick={() => handleNavigation(feature?.link)}
                            aria-label={`Navigate to ${feature.title}`}
                        >
                            {feature.buttonText || 'Get Started'}
                        </button>
                    </div>
                </div>
                {(isEven && !isNarrowViewport) && (
                    <div className={imageContainerClass}>
                        <div className={ellipseClass} aria-hidden="true"></div>
                        <div className={imageClass}>
                            <div className='img-container'>
                                <img
                                    src={feature.image}
                                    alt={feature.altText}
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [handleNavigation, isNarrowViewport]);

    return (
        <div className={`${className} directory-landing-container landing-page footer-margin-offset`}>
            <Helmet
                title={pageMetadata.pageTitle || pageMetadata.title}
                link={[
                    {
                        rel: 'canonical',
                        href: `${WEB_URL}${pageMetadata.url}`,
                    },
                ]}
                meta={[
                    {
                        name: 'description',
                        content: pageMetadata.description,
                    },
                    {
                        name: 'abstract',
                        content: pageMetadata.description,
                    },
                    {
                        property: 'og:type',
                        content: 'website',
                    },
                    {
                        property: 'og:url',
                        content: `${WEB_URL}${pageMetadata.url}`,
                    },
                    {
                        property: 'og:title',
                        content: pageMetadata.pageTitle || pageMetadata.title,
                    },
                    {
                        property: 'og:description',
                        content: pageMetadata.description,
                    },
                ]}
                script={[
                    {
                        type: 'application/ld+json',
                        innerHTML: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "WebPage",
                            "name": pageMetadata.title,
                            "description": pageMetadata.description,
                            "url": `${WEB_URL}${pageMetadata.url}`
                        }),
                    },
                ]}
            />

            <section className="hero-section" role="banner">
                <div className="hero-content">
                    <h1>{pageMetadata.title}</h1>
                    <p>{pageMetadata.description}</p>
                    <button
                        className="btn btn-main btn-lg"
                        onClick={showContactForm}
                        aria-label="Open contact form to get started"
                    >
                        {pageMetadata.heroButtonText || 'Get Started'}
                    </button>
                </div>
            </section>

            <section className="feature-section">
                {featuresData.map((feature, index) => FeatureSection({ feature, index }))}
            </section>

            {showVideoSection && <section className='landing-page-video'>
                <div className='landing-page-video-container'>
                    <div className='landing-page-video-title'>
                        <h2>See Our Platform in Action</h2>
                    </div>

                    <div className='landing-page-video-frame'>
                        <VideoHandler
                            src={`${PUBLIC_IMG_URL}/directory-landing/MAGNiTT-Directories-Canvid.mp4`}
                            title="See Advanced Analytics in Action"
                            className="landing-page-video-lite"
                            preloadOnView={false}
                            rootMargin="200px"
                            poster={`${PUBLIC_IMG_URL}/directory-landing/MAGNiTT-Directories-Canvid-review-image.webp`}
                        />
                    </div>
                </div>
            </section>}

            <section className="pricing-contact-form" id="contactForm">
                <div className="row">
                    <ContactForm
                        taxonomyType={TAXONOMIES.salesContactOptions}
                        formType={contactFormConfig?.formType || CONTACT_FORM_TYPE.enterprise}
                        buttonLabel={contactFormConfig?.buttonLabel || 'Book a discovery call'}
                        page={contactFormConfig?.page || HUBSPOT_FORM_SOURCE.pricing_contact_sales}
                        headerText={contactFormConfig?.headerText || (
                            <p className="pricing-form-header">
                                <span className="mb-2">Interested in learning more?</span>
								Use MAGNiTT to make informed investments and strengthen your team's competitive position
                            </p>
                        )}
                    />
                    <div className="col-md-10 col-lg-7 col-xl-7 mx-auto mb-4 mb-lg-0">
                        <div className="db-box no-background h-100">
                            <div className="text-section">
                                <div className="pricing-clients">
                                    <div className="row">
                                        <p className="pricing-form-header text-center">
											Our data is trusted by
                                            <span>the world's leading companies</span>
                                        </p>
                                        {CLIENT_LOGOS.map((logo, index) => (
                                            <div className="col-6 col-md-4 col-lg-5" key={`logo-${index}`}>
                                                <div className="img-container">
                                                    <img
                                                        className="img-fluid"
                                                        src={logo}
                                                        alt={`Trusted company logo ${index + 1}`}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Modals */}
            <SuccessPopup
                msg={SALES_CONTACT_SUCCESS_MESSAGE}
                handleClose={() => { }}
            />
            <ContactSalesPopup page={contactFormConfig?.contactSalesPage || HUBSPOT_FORM_SOURCE.startups_dir_locked_section} />
        </div>
    );
};

export default LandingPageComponent;
